/**
 * Phase 36 — the experiment's words, shared by the result card and the
 * backend's judge: the metric's name, a value in its unit, the winner and
 * the sentence a result card and a feed line both print.
 * For Media Devoted | Created By Ciro Autiero
 */
import {
  type DropGenExperimentAction,
  type DropGenExperimentMetric,
  type DropGenExperimentResult,
  type DropGenExperimentSide,
  EXPERIMENT_LOWER_WINS,
} from "./experiment.types";

export const EXPERIMENT_METRIC_WORDS: Record<DropGenExperimentMetric, string> =
  {
    deliveredRate: "delivered rate",
    botRate: "bot rate",
    realCtr: "real CTR",
    undeliverableRate: "undeliverable rate",
    ctr: "CTR",
    cv: "CV",
  };

/** A metric value in words — rates stored 0–1 print as %, ctr/cv are % already. */
export const experimentValue = (
  metric: DropGenExperimentMetric,
  v: number | null,
): string =>
  v === null
    ? "—"
    : `${Math.round((metric === "ctr" || metric === "cv" ? v : v * 100) * 10) / 10} %`;

export const lowerWins = (metric: DropGenExperimentMetric): boolean =>
  EXPERIMENT_LOWER_WINS.includes(metric);

/** Who is ahead: the better side on the metric's direction; equal → tie. */
export function experimentWinner(
  metric: DropGenExperimentMetric,
  experiment: number | null,
  others: number | null,
): DropGenExperimentResult["winner"] {
  if (experiment === null || others === null) return null;
  if (experiment === others) return "tie";
  const better = lowerWins(metric) ? experiment < others : experiment > others;
  return better ? "experiment" : "others";
}

/** The experiment gateway vs the others, in % of the others' value, signed better / worse. */
export function experimentDelta(
  metric: DropGenExperimentMetric,
  experiment: number | null,
  others: number | null,
): number | null {
  if (experiment === null || others === null || others === 0) return null;
  const raw = ((experiment - others) / Math.abs(others)) * 100;
  return Math.round((lowerWins(metric) ? -raw : raw) * 10) / 10;
}

const side = (
  metric: DropGenExperimentMetric,
  s: DropGenExperimentSide,
): string =>
  `${s.label}: ${EXPERIMENT_METRIC_WORDS[metric]} ${experimentValue(metric, s.value)} (${s.smsSent.toLocaleString("en-US")} SMS · ${s.drops} drop${s.drops === 1 ? "" : "s"})`;

/** "C: delivered rate 80 % (3,000 SMS · 3 drops) vs A/B: 92 % (…) — C loses by 13 %". */
export function experimentWords(
  r: Omit<DropGenExperimentResult, "words" | "judgedAt">,
): string {
  const head = `${side(r.metric, r.experiment)} vs ${side(r.metric, r.others)}`;
  if (r.winner === null) return `${head} — no comparison yet`;
  if (r.tooEarly)
    return `${head} — too early to call (under the minimum SMS on one side)`;
  if (r.winner === "tie") return `${head} — a tie`;
  const pct = r.deltaPct === null ? "" : ` by ${Math.abs(r.deltaPct)} %`;
  return `${head} — ${r.experiment.label} ${r.winner === "experiment" ? "wins" : "loses"}${pct}`;
}

export const EXPERIMENT_ACTION_WORDS: Record<
  DropGenExperimentAction,
  (gateway: string, sharePct: number) => string
> = {
  first: (g) => `make ${g} first in the pool`,
  out: (g) => `take ${g} out of the pool`,
  share: (g, pct) => `give ${g} ${pct} % by manual weights`,
};
